import type {
  RecordsTableColumn,
  RecordsTableFieldColumn,
  RecordsTableStatusColumn,
} from '@cims-online/shared'
import type { ColumnType } from 'antd/lib/table'
import HelpIcon from 'components/HelpIcon'
import type { FilterOptions } from 'hooks/useTableFilter/types'
import type { ReactNode } from 'react'
import ApprovalFieldCell from './components/ApprovalFieldCell'
import { createApprovalActionTarget } from '../useApprovalActions/recordValues'
import type { WorkflowRecord } from '../../types'
import type { ApprovalActionController } from '../useApprovalActions/types'
import { findFieldValue, statusText } from './fieldValues'
import { fieldFilter, statusFilter } from './filter'
import { renderFieldValue } from './renderFieldValue'
import { fieldSort, statusSort } from './sort'

type TableFilterResolver = (
  filter: FilterOptions<WorkflowRecord>
) => ColumnType<WorkflowRecord>

function tableCell(children: ReactNode) {
  return <div style={{ overflow: 'clip' }}>{children}</div>
}

function statusColumn(
  column: RecordsTableStatusColumn,
  tableFilter: TableFilterResolver
): ColumnType<WorkflowRecord> {
  return {
    key: 'status',
    title: <HelpIcon text={column.help}>{column.title}</HelpIcon>,
    width: column.width,
    render: (_value, record) => tableCell(statusText(record)),
    showSorterTooltip: false,
    ...statusSort(column),
    ...tableFilter(statusFilter()),
  }
}

/** Keeps saved approval decisions visible independently of action availability. */
function fieldColumn(
  column: RecordsTableFieldColumn,
  tableFilter: TableFilterResolver,
  approvalController: ApprovalActionController | undefined
): ColumnType<WorkflowRecord> {
  const filter = fieldFilter(column)
  return {
    key: `${column.fieldId}:${column.displayValue}`,
    title: <HelpIcon text={column.help}>{column.title}</HelpIcon>,
    width: column.width,
    render: (_value, record) => {
      const fieldValue = findFieldValue(record, column.fieldId)?.value
      const approvalActions = record.approvalActions
      if (column.displayValue === 'value' && fieldValue?.type === 'approvals')
        return tableCell(
          <ApprovalFieldCell
            approvalActions={
              approvalActions?.approvalsFieldId === column.fieldId
                ? approvalActions
                : null
            }
            fieldValue={fieldValue}
            onAction={
              approvalController
                ? (optionId, action) =>
                    approvalController.onAction(
                      createApprovalActionTarget(record, optionId, action)
                    )
                : undefined
            }
            pending={approvalController?.pending}
            pendingAction={approvalController?.pendingAction}
            recordId={record.id}
          />
        )
      return tableCell(renderFieldValue(fieldValue, column.displayValue))
    },
    showSorterTooltip: false,
    ...fieldSort(column),
    ...(filter ? tableFilter(filter) : undefined),
  }
}

/**
 * Translates one server-owned column without choosing fields or defaults on the
 * client. The discriminating `type` only selects the appropriate mechanics.
 */
export function toColumn(
  column: RecordsTableColumn,
  tableFilter: TableFilterResolver,
  approvalController?: ApprovalActionController
): ColumnType<WorkflowRecord> {
  if (column.type === 'status') return statusColumn(column, tableFilter)
  return fieldColumn(column, tableFilter, approvalController)
}
